import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  isNilOrZero,
} from '@populateui/utils';
import {
  EnrollPatientInPCCProgramsRequestDTO,
  EncounterSummaryViewModel,
  None,
  OptInPatientToCCMRequestDTO,
  PatientPCCConsentsDTO,
  PatientPCCStatusDTO,
  PCCCcmCallDTO,
  PCCOrgActivationSettingsDTO,
  PCCPatientDetailResponseDTO,
  PostApiPCCCommuniqueSendByPatientIdParams,
  SendPatientPCCConsentRequestDTO,
  SetPCCOrgActivationSettingsRequestDTO,
  UpdatePatientPCCConsentsRequestDTO,
} from 'dtos';
import {
  OrgPCCActivationStatus,
} from 'dtos/orgPCCActivationStatus';
import {
  getApiPatientGetPCCStatusByPatientIdPatientId,
  getApiPCCActivationGetSettings,
  getApiPCCConsentGetByPatientId,
  getApiRTMMonitoringGetEncountersForRTMRecommendation,
  postApiPCCActivationSetSettings,
  postApiPCCCommuniqueSendByPatientId,
  postApiPCCConsentEnrollPatient,
  postApiPCCConsentOptInToCCM,
  postApiPCCConsentSendPCCConsent,
  postApiPCCDashboardGetCcmCallsByPatientId,
  postApiPCCDashboardGetPatientDetailsByPatientId,
  putApiPCCConsentUpdate,
} from 'endpoints';
import {
  serializeDateRange,
} from 'pages/Dashboard/transport/dateRange';
import {
  DateRange,
} from 'types/dateRange';

export const pccActivationSettingsQueryKey = ['pcc-activation-settings'] as const;

export const pccPatientStatusQueryKey = (patientId: number) => [
  'pcc-patient-status',
  patientId,
] as const;

/*
const query = useQuery<PCCOrgActivationSettingsDTO, Error>({
    queryKey: pccActivationSettingsQueryKey,
    queryFn: () => {
      const result = getApiPCCActivationGetSettings();
      console.log('result', result);
      return result.then((result) => {
        console.log('result', result);
        return {
          ...result,
          isCcmApplicable: true,
        };
      });
    },
  });
*/

export function useGetPCCActivationSettings(): UseQueryResult<
  PCCOrgActivationSettingsDTO,
  Error
> {
  return useQuery<PCCOrgActivationSettingsDTO, Error>({
    queryKey: pccActivationSettingsQueryKey,
    queryFn: () => getApiPCCActivationGetSettings(),
  });
}

export function useSetPCCActivationSettings(): UseMutationResult<
  PCCOrgActivationSettingsDTO,
  Error,
  SetPCCOrgActivationSettingsRequestDTO
> {
  const queryClient = useQueryClient();

  return useMutation<
    PCCOrgActivationSettingsDTO,
    Error,
    SetPCCOrgActivationSettingsRequestDTO
  >({
    mutationFn: (settings) => postApiPCCActivationSetSettings(settings),
    onSuccess: () => queryClient.invalidateQueries({
      queryKey: pccActivationSettingsQueryKey,
    }),
  });
}

export function useGetPCCConsentsByPatientId(
  patientId: number = 0,
): UseQueryResult<PatientPCCConsentsDTO, Error> {
  return useQuery<PatientPCCConsentsDTO, Error>({
    queryKey: ['pcc-consents', patientId],
    queryFn: () => getApiPCCConsentGetByPatientId({ patientId }),
    enabled: !isNilOrZero(patientId),
  });
}

export function useUpdatePCCConsents(): UseMutationResult<
  PatientPCCConsentsDTO,
  Error,
  UpdatePatientPCCConsentsRequestDTO
> {
  return useMutation<
    PatientPCCConsentsDTO,
    Error,
    UpdatePatientPCCConsentsRequestDTO
  >({
    mutationFn: (payload) => putApiPCCConsentUpdate(payload),
  });
}

export function useSendPCCConsent(): UseMutationResult<
  None,
  Error,
  SendPatientPCCConsentRequestDTO
> {
  return useMutation<None, Error, SendPatientPCCConsentRequestDTO>({
    mutationFn: (payload) => postApiPCCConsentSendPCCConsent(payload),
  });
}

export function useSendPCCReminder(): UseMutationResult<
  None,
  Error,
  PostApiPCCCommuniqueSendByPatientIdParams
> {
  return useMutation<None, Error, PostApiPCCCommuniqueSendByPatientIdParams>({
    mutationFn: (params) => postApiPCCCommuniqueSendByPatientId(params),
  });
}

export function useGetPCCStatusByPatientId(
  patientId: number = 0,
  enabled: boolean = true,
): UseQueryResult<PatientPCCStatusDTO, Error> {
  return useQuery<PatientPCCStatusDTO, Error>({
    queryKey: pccPatientStatusQueryKey(patientId),
    queryFn: () => getApiPatientGetPCCStatusByPatientIdPatientId(patientId),
    enabled: enabled && !isNilOrZero(patientId),
  });
}

export function useEnrollPatientInPCC(): UseMutationResult<
  None,
  Error,
  EnrollPatientInPCCProgramsRequestDTO
> {
  return useMutation<None, Error, EnrollPatientInPCCProgramsRequestDTO>({
    mutationFn: (payload) => postApiPCCConsentEnrollPatient(payload),
  });
}

export function useGetEncountersForRTMRecommendation(
  patientId: number = 0,
): UseQueryResult<EncounterSummaryViewModel[], Error> {
  return useQuery<EncounterSummaryViewModel[], Error>({
    queryKey: ['rtm-encounters-for-recommendation', patientId],
    queryFn: () => getApiRTMMonitoringGetEncountersForRTMRecommendation({
      patientId,
    }),
    enabled: !isNilOrZero(patientId),
  });
}

export function useGetPCCPatientDetails(
  patientId: number,
  dateRange: DateRange,
): UseQueryResult<PCCPatientDetailResponseDTO, Error> {
  const serializedDateRange = serializeDateRange(dateRange);

  return useQuery<PCCPatientDetailResponseDTO, Error>({
    queryKey: [
      'pcc-patient-detail',
      patientId,
      serializedDateRange?.start,
      serializedDateRange?.end,
    ],
    queryFn: () => postApiPCCDashboardGetPatientDetailsByPatientId({
      patientId,
      dateRange: serializedDateRange,
    }),
    enabled: patientId > 0,
  });
}

export function useGetCcmCallsByPatientId(
  patientId: number,
  dateRange: DateRange,
): UseQueryResult<PCCCcmCallDTO[], Error> {
  const serializedDateRange = serializeDateRange(dateRange);

  return useQuery<PCCCcmCallDTO[], Error>({
    queryKey: [
      'pcc-ccm-calls',
      patientId,
      serializedDateRange?.start,
      serializedDateRange?.end,
    ],
    queryFn: () => postApiPCCDashboardGetCcmCallsByPatientId({
      patientId,
      dateRange: serializedDateRange,
    }),
    enabled: patientId > 0,
  });
}

export function useOptInPatientToCCM(): UseMutationResult<
  None,
  Error,
  OptInPatientToCCMRequestDTO
> {
  return useMutation<None, Error, OptInPatientToCCMRequestDTO>({
    mutationFn: (payload) => postApiPCCConsentOptInToCCM(payload),
  });
}

export type PCCAvailability = {
  isLoading: boolean;
  isError: boolean;
  isPCCAvailable: boolean;
  isPCCPending: boolean;
  isCcmAvailable: boolean;
};

export function usePCCAvailability(): PCCAvailability {
  const {
    data: activationSettings,
    isLoading,
    isError,
  } = useGetPCCActivationSettings();
  const activationStatus = activationSettings?.activationStatus;
  const isPCCAvailable = activationStatus === OrgPCCActivationStatus.Active;
  const isPCCPending = activationStatus === OrgPCCActivationStatus.Pending;

  return {
    isLoading,
    isError,
    isPCCAvailable,
    isPCCPending,
    isCcmAvailable: (
      isPCCAvailable
      && activationSettings?.isCcmApplicable === true
    ),
  };
}
